import React, {ChangeEvent, useState} from 'react'

import {ImageCarouselModal} from './ImageCarouselModal'
import {KTSVG} from '../_metronic/helpers'
import {toast} from 'react-toastify'

export interface FileWithPreview {
  id: string
  file?: File
  preview: string
  isNew?: boolean
}

interface MultiFilePickerProps {
  images: FileWithPreview[]
  setImages: React.Dispatch<React.SetStateAction<FileWithPreview[]>>
  isDemandePj?: boolean
  isTraitementDemande?: boolean
}

export function MultiFilePicker({
  images,
  setImages,
  isDemandePj = false,
  isTraitementDemande = false,
}: MultiFilePickerProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5 Mo

  const addFiles = (fileList: FileList) => {
    if (!fileList.length) return

    const newFiles: FileWithPreview[] = Array.from(fileList).map((file) => ({
      id: crypto.randomUUID(),
      file,
      preview: URL.createObjectURL(file),
      isNew: true,
    }))

    //le fichier ne doit pas dépasser 5Mo
    const file_Size = newFiles[0]?.file?.size ?? 0
    if (file_Size > MAX_FILE_SIZE) {
      toast.error('Le fichier ne doit pas dépasser 5 Mo.')
      return
    }

    const file_type: any = newFiles[0]?.file?.type
    if (!file_type.startsWith('image/') && file_type !== 'application/pdf') {
      toast.error('Seuls les fichiers image et PDF sont autorisés.')
      return
    }

    setImages((prev) => [...prev, ...newFiles])
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return

    addFiles(e.target.files)

    // Permet de sélectionner à nouveau
    // le même fichier
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()

    if (!e.dataTransfer.files?.length) return

    addFiles(e.dataTransfer.files)
  }

  const removeImage = (id: string, preview: string, isNew?: boolean) => {
    if (isNew) {
      URL.revokeObjectURL(preview)
    }

    setImages((prev) => prev.filter((image) => image.id !== id))

    setSelectedIndex((currentIndex) => {
      if (currentIndex === null) {
        return null
      }

      const removedIndex = images.findIndex((image) => image.id === id)

      if (removedIndex === -1) {
        return currentIndex
      }

      // Il n'y avait qu'une seule image
      if (images.length === 1) {
        return null
      }

      // Une image située avant l'image courante
      // vient d'être supprimée
      if (currentIndex > removedIndex) {
        return currentIndex - 1
      }

      // L'image actuellement affichée est supprimée
      if (currentIndex === removedIndex) {
        return Math.min(currentIndex, images.length - 2)
      }

      return currentIndex
    })
  }

  const imageUrls = images.map((image) => image.preview)
  const imagesObj = images.length
    ? images.map((image) => ({
        preview: image?.preview,
        type: image?.file?.type,
      }))
    : []

  return (
    <div
      style={{
        marginBottom: '20px',
      }}
    >
      {!isTraitementDemande && (
        <label
          onDragOver={isDemandePj ? (e) => e.preventDefault() : undefined}
          onDrop={isDemandePj ? handleDrop : undefined}
          style={{
            display: 'flex',
            textAlign: 'center',
            padding: '12px',
            background: '#f3f4f6',
            color: '#4b5563',
            border: '2px dashed #d1d5db',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          {isDemandePj ? (
            <>
              <div
                style={
                  {
                    // fontSize: '14px'
                  }
                }
              >
                <KTSVG path='/media/svg/files/cloud-upload.svg' className='svg-icon svg-icon-2x' />{' '}
                Glissez-déposez vos fichiers ici ou
              </div>
              <div
                style={{
                  // fontSize: '14px',
                  fontWeight: '400',
                  color: '#6b7280',
                }}
              >
                <span
                  style={{
                    color: '#2563eb',
                    fontWeight: '600',
                  }}
                >
                  cliquez pour parcourir
                </span>
              </div>
            </>
          ) : (
            '＋ Ajouter des photos'
          )}
          <input
            type='file'
            multiple
            accept={isDemandePj ? 'image/*,application/pdf' : 'image/*'}
            onChange={handleFileChange}
            style={{
              display: 'none',
            }}
          />
        </label>
      )}

      {/* Grille des miniatures */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isDemandePj ? 'repeat(12, 1fr)' : 'repeat(4, 1fr)',
          gap: '8px',
          marginTop: '15px',
        }}
      >
        {images.map(({id, preview, file, isNew}, index) => (
          <div
            key={id}
            style={{
              position: 'relative',
              aspectRatio: '1',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              width: isDemandePj ? '100px' : '',
            }}
          >
            {/* Image */}
            <button
              type='button'
              onClick={() => setSelectedIndex(index)}
              style={{
                width: '100%',
                height: '100%',
                padding: 0,
                border: 0,
                background: 'transparent',
                cursor: 'zoom-in',
              }}
              aria-label={file?.name ?? `Afficher la photo ${index + 1}`}
            >
              {file?.type === 'application/pdf' ? (
                <div className='pdf-thumbnail d-flex flex-column'>
                  <span style={{fontSize: 40}}>📄</span>
                  <span>{file.name}</span>
                </div>
              ) : (
                <img
                  src={preview}
                  alt={file?.name ?? `Photo ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              )}
            </button>

            {/* Bouton supprimer */}
            {!isTraitementDemande && (
              <button
                type='button'
                onClick={() => removeImage(id, preview, isNew)}
                style={{
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: '22px',
                  height: '22px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(4px)',
                }}
                title='Supprimer'
                aria-label={
                  file?.name ? `Supprimer ${file.name}` : `Supprimer la photo ${index + 1}`
                }
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Carousel */}
      {selectedIndex !== null && (
        <ImageCarouselModal
          images={imageUrls}
          initialIndex={selectedIndex}
          onClose={() => setSelectedIndex(null)}
          imagesObj={imagesObj}
        />
      )}
    </div>
  )
}
